(function() {
    'use strict';

    angular
        .module('atheer.services')
        .factory('SessionExternalInvitee', SessionExternalInvitee);

    /* @ngInject */
    function SessionExternalInvitee($q, Principal, Setting, Workspace, Audience, $filter) {
        var cachedContextPromise = null;
        var cachedContext = null;
        var nameResolutionHost = null;
        var MASKED_NAME_FALLBACK_KEY = 'atheer.session.maskedDisplayNameFallback';
        var MEMBERS_PAGE_SIZE = 200;

        var service = {
            loadInviteContext: loadInviteContext,
            getCachedContext: getCachedContext,
            setNameResolutionHost: setNameResolutionHost,
            getNameResolutionHost: getNameResolutionHost,
            enrichHostForNameResolution: enrichHostForNameResolution,
            applySessionHostFlags: applySessionHostFlags,
            stampHostSessionFlags: stampHostSessionFlags,
            stampSessionHostObject: stampSessionHostObject,
            applySessionHostTransfer: applySessionHostTransfer,
            sanitizeSessionApiFlags: sanitizeSessionApiFlags,
            parseTeamIdsString: parseTeamIdsString,
            isRestrictedExternalHost: isRestrictedExternalHost,
            isExternalHost: isExternalHost,
            buildMembersQuery: buildMembersQuery,
            decorateInviteeList: decorateInviteeList,
            decorateInvitee: decorateInvitee,
            decorateUsersForSessionDetail: decorateUsersForSessionDetail,
            filterMembersLocally: filterMembersLocally,
            paginateMembers: paginateMembers,
            resolvePublishedDisplayName: resolvePublishedDisplayName,
            resolveVisibleDisplayName: resolveVisibleDisplayName,
            resolveCurrentUserPublishedName: resolveCurrentUserPublishedName,
            applyVisibleNameToUserInfo: applyVisibleNameToUserInfo,
            applyVisibleNameToLiveSessionUser: applyVisibleNameToLiveSessionUser,
            getMaskedDisplayName: getMaskedDisplayName,
            classifyUser: classifyUser,
            isExternalUser: isExternalUser,
            getSessionUserMaskFlag: getSessionUserMaskFlag,
            getSessionUserMaskedDisplayName: getSessionUserMaskedDisplayName,
            shouldPublishMaskedName: shouldPublishMaskedName,
            buildShortNameFromDisplayName: buildShortNameFromDisplayName,
            resolveApiVisibleDisplayName: resolveApiVisibleDisplayName,
            applyApiVisibleNameToUserInfo: applyApiVisibleNameToUserInfo,
            shouldUseMaskedNameForApiUser: shouldUseMaskedNameForApiUser
        };

        return service;

        /**
         * Loads session settings, workspace domain, and configured support-team
         * members via audiences/members?include_details=true (UserDTO + segments).
         */
        function loadInviteContext(forceRefresh) {
            if (!forceRefresh && cachedContextPromise) {
                return cachedContextPromise;
            }

            if (forceRefresh) {
                cachedContextPromise = null;
                cachedContext = null;
            }

            var userInfo = Principal.getUserInfo() || {};
            var workspaceInfo = userInfo.workspace_info || {};
            var workspaceId = workspaceInfo.id || workspaceInfo.code || Principal.getWorkspaceCode();
            var domainFallback = (
                workspaceInfo.email_domain ||
                workspaceInfo.domain ||
                Principal.getWorkspaceDomain() ||
                ''
            ).toLowerCase();

            // Workspace fetch must not block invite-context; settings alone are enough
            // to decide restriction when domain comes from identity fallback.
            cachedContextPromise = $q
                .all({
                    settings: Setting.getSessionSettings().$promise,
                    workspace: Workspace.get({ id: workspaceId }).$promise.catch(function() {
                        return null;
                    })
                })
                .then(function(result) {
                    var parsedSettings = parseSessionSettings(result.settings || []);
                    var teamIds = Object.keys(parsedSettings.teamMaskMap);
                    var workspaceEmailDomain = (
                        ((result.workspace || {}).email_domain || '') ||
                        domainFallback
                    ).toLowerCase();
                    var baseContext = {
                        currentUser: userInfo,
                        workspaceEmailDomain: workspaceEmailDomain,
                        externalUserInviteeListEnabled: parsedSettings.externalUserInviteeListEnabled,
                        teamMaskMap: parsedSettings.teamMaskMap,
                        teamIds: teamIds,
                        supportMembers: [],
                        supportMemberMap: {},
                        isSupportTeamMember: false,
                        isRestrictedExternal: false
                    };

                    if (!parsedSettings.externalUserInviteeListEnabled || !teamIds.length) {
                        cachedContext = baseContext;
                        return baseContext;
                    }

                    return fetchAllSupportMembers(teamIds, parsedSettings.teamMaskMap).then(function(members) {
                        var supportMemberMap = buildSupportMemberMap(members, parsedSettings.teamMaskMap);
                        var supportMembers = Object.keys(supportMemberMap).map(function(id) {
                            return supportMemberMap[id];
                        });
                        var currentUserId = userInfo.id || Principal.getUserId();
                        var isSupport = !!supportMemberMap[currentUserId];

                        baseContext.supportMembers = supportMembers;
                        baseContext.supportMemberMap = supportMemberMap;
                        baseContext.isSupportTeamMember = isSupport;
                        baseContext.isRestrictedExternal = isRestrictedExternalHost({
                            currentUser: userInfo,
                            externalUserInviteeListEnabled: true,
                            teamIds: teamIds,
                            workspaceEmailDomain: workspaceEmailDomain,
                            isSupportTeamMember: isSupport
                        });

                        cachedContext = baseContext;
                        return baseContext;
                    });
                });

            return cachedContextPromise;
        }

        function getCachedContext() {
            return cachedContext;
        }

        function setNameResolutionHost(host) {
            nameResolutionHost = host || null;
        }

        function getNameResolutionHost() {
            return nameResolutionHost;
        }

        /**
         * Merges Principal into owner_info when the local user is the session owner.
         * owner_info from for_launch often lacks user_type/email, which breaks live remask.
         * When both exist, Principal wins for classification fields (owner_info can be stale).
         */
        function enrichHostForNameResolution(host, session) {
            var resolved = angular.extend({}, host || {});
            var me = Principal.getUserInfo() || {};
            var ownerId = (session && session.owner) || resolved.id || resolved.user_id;

            // Always bind session.owner onto the host object. for_launch owner_info
            // often omits id; without it joiners were mis-classified as the host.
            if (ownerId) {
                resolved.id = resolved.id || ownerId;
                resolved.user_id = resolved.user_id || ownerId;
            }

            if (me.id && ownerId && String(me.id) === String(ownerId)) {
                resolved.id = me.id || resolved.id;
                resolved.user_id = me.id || resolved.user_id;
                resolved.email = me.email || resolved.email;
                resolved.user_type = me.user_type || resolved.user_type;
                resolved.first_name = me.first_name || resolved.first_name;
                resolved.last_name = me.last_name || resolved.last_name;
            }

            return resolved;
        }

        
         
        function getSessionIsExternalHostFlag(session) {
            if (!session) {
                return undefined;
            }
            if (typeof session.is_external_host === 'boolean') {
                return session.is_external_host;
            }
            if (typeof session.isExternalHost === 'boolean') {
                return session.isExternalHost;
            }
            if (typeof session.externalHost === 'boolean') {
                return session.externalHost;
            }
            return undefined;
        }

        function setSessionIsExternalHostFlag(session, value) {
            if (!session) {
                return;
            }
            // Session REST: snake_case only. CamelCase is for PubNub/Jitsi live objects, not API.
            session.is_external_host = !!value;
            delete session.isExternalHost;
        }

        function getEntryUseMaskedNameFlag(entry) {
            if (!entry) {
                return undefined;
            }
            if (typeof entry.use_masked_name === 'boolean') {
                return entry.use_masked_name;
            }
            if (typeof entry.useMaskedName === 'boolean') {
                return entry.useMaskedName;
            }
            return undefined;
        }

        function setEntryUseMaskedNameFlag(entry, value) {
            if (!entry) {
                return;
            }
            entry.use_masked_name = !!value;
            delete entry.useMaskedName;
        }

        /**
         * Before Session.save/update: keep only API field names on the wire.
         * Reads camelCase/externalHost if present, writes snake_case, strips aliases.
         * Does not touch live PubNub sessionUsers (separate in-memory objects).
         */
        function sanitizeSessionApiFlags(session) {
            if (!session) {
                return session;
            }

            var hostFlag = getSessionIsExternalHostFlag(session);
            if (typeof hostFlag === 'boolean') {
                session.is_external_host = hostFlag;
            }
            delete session.isExternalHost;
            // Response-only alias from some backends — do not re-send.
            delete session.externalHost;

            angular.forEach(session.session_users || [], function(entry) {
                if (!entry) {
                    return;
                }
                var maskFlag = getEntryUseMaskedNameFlag(entry);
                if (typeof maskFlag === 'boolean') {
                    entry.use_masked_name = maskFlag;
                }
                delete entry.useMaskedName;
            });

            if (session.session_host) {
                sanitizeSessionHostObject(session.session_host);
            }

            return session;
        }

        function sanitizeSessionHostObject(hostObj) {
            if (!hostObj) {
                return hostObj;
            }
            if (typeof hostObj.use_masked_name !== 'boolean' &&
                typeof hostObj.useMaskedName === 'boolean') {
                hostObj.use_masked_name = hostObj.useMaskedName;
            }
            delete hostObj.useMaskedName;
            return hostObj;
        }

        /**
         * Initial host stamp. creator_user_id stays stable once set.
         * Master Scheduler may pass creatorId (scheduler) + useMaskedName for support host.
         */
        function stampSessionHostObject(session, options) {
            if (!session) {
                return session;
            }
            var opts = options || {};
            var ownerId = session.owner || opts.ownerId || null;
            var existing = session.session_host || {};

            if (existing.creator_user_id && opts.creatorId == null && opts.useMaskedName == null) {
                session.session_host = {
                    user_id: String(ownerId || existing.user_id || existing.creator_user_id),
                    creator_user_id: String(existing.creator_user_id),
                    use_masked_name: typeof existing.use_masked_name === 'boolean'
                        ? existing.use_masked_name
                        : false
                };
                return session;
            }

            var creatorId = opts.creatorId || existing.creator_user_id || ownerId || null;
            var useMasked = typeof opts.useMaskedName === 'boolean'
                ? opts.useMaskedName
                : (typeof existing.use_masked_name === 'boolean'
                    ? existing.use_masked_name
                    : false);

            session.session_host = {
                use_masked_name: !!useMasked
            };
            if (ownerId) {
                session.session_host.user_id = String(ownerId);
            }
            if (creatorId) {
                session.session_host.creator_user_id = String(creatorId);
            }
            return session;
        }

        /**
         * set use_masked_name from new host's participant mask flag.
         */
        function applySessionHostTransfer(session, newHostUserId, newHostUseMaskedName) {
            if (!session || !newHostUserId) {
                return session;
            }
            var existing = session.session_host || {};
            var creatorId = existing.creator_user_id || session.owner || null;
            var useMasked = typeof newHostUseMaskedName === 'boolean'
                ? newHostUseMaskedName
                : (getSessionUserMaskFlag(session, newHostUserId) === true);

            session.session_host = {
                user_id: String(newHostUserId),
                creator_user_id: creatorId ? String(creatorId) : String(newHostUserId),
                use_masked_name: !!useMasked
            };
            return session;
        }

        /**
         * EXTERNAL user, not workspace-domain email, not support team member.
         * Same rule as invite restriction (isRestrictedExternalHost).
         *
         * Master Scheduler only: when session.scheduler is set and differs from
         * owner, classify the scheduler (creator) — not the assigned support host.
         */
        function resolveSessionIsExternalHost(session, context) {
            var ctx = context || cachedContext || {};
            if (!ctx.externalUserInviteeListEnabled || !(ctx.teamIds || []).length) {
                return false;
            }

            var ownerId = session && session.owner;
            var schedulerId = session && session.scheduler &&
                (session.scheduler.id || session.scheduler.user_id);
            var existing = getSessionIsExternalHostFlag(session);
            var me = Principal.getUserInfo() || {};

            // Scheduling-only: Master Scheduler assigned a different host.
            if (schedulerId && ownerId && String(schedulerId) !== String(ownerId)) {
                if (String(me.id) === String(schedulerId)) {
                    return isRestrictedExternalHost({
                        currentUser: me,
                        externalUserInviteeListEnabled: true,
                        teamIds: ctx.teamIds || [],
                        workspaceEmailDomain: ctx.workspaceEmailDomain,
                        isSupportTeamMember: !!ctx.isSupportTeamMember
                    });
                }
                // Prefer API flag when we cannot classify the scheduler payload.
                if (typeof existing === 'boolean') {
                    return existing;
                }
            }

            var host = enrichHostForNameResolution(
                (session && session.owner_info) || {},
                session
            );
            var hostId = host.id || host.user_id || ownerId;
            var creatorId = session && session.session_host &&
                session.session_host.creator_user_id;

            // Keep session-level is_external_host after Make Host. The flag is
            // initiation-scoped and must survive transfer to internal or support
            // (live masking + Insights Participant List / Activity Log).
            if (existing === true && creatorId && hostId &&
                String(creatorId) !== String(hostId)) {
                return true;
            }

            // Never fall back to Principal (the joiner).
            if (!host.user_type && !host.email) {
                if (typeof existing === 'boolean') {
                    return existing;
                }
            }

            var isSupport = false;
            if (hostId && ctx.supportMemberMap) {
                isSupport = !!(
                    ctx.supportMemberMap[hostId] ||
                    ctx.supportMemberMap[String(hostId)]
                );
            } else if (hostId && ctx.isSupportTeamMember &&
                String((ctx.currentUser || {}).id) === String(hostId)) {
                isSupport = true;
            }

            return isRestrictedExternalHost({
                currentUser: host,
                externalUserInviteeListEnabled: true,
                teamIds: ctx.teamIds || [],
                workspaceEmailDomain: ctx.workspaceEmailDomain,
                isSupportTeamMember: !!isSupport
            });
        }

        /**
         * Stamps session.is_external_host from invite context when available.
         * Once true, keeps it for the session lifetime (initiation-scoped — must
         * survive Make Host to internal/support and end-session PUT for Insights).
         */
        function applySessionHostFlags(session, context) {
            if (!session) {
                return session;
            }

            var ctx = context || cachedContext || {};
            var existing = getSessionIsExternalHostFlag(session);
            if (existing === true) {
                setSessionIsExternalHostFlag(session, true);
                if (ctx) {
                    ctx.sessionIsExternalHost = true;
                }
                return session;
            }

            if (ctx.externalUserInviteeListEnabled && (ctx.teamIds || []).length) {
                var resolved = resolveSessionIsExternalHost(session, ctx);
                setSessionIsExternalHostFlag(session, resolved);
                ctx.sessionIsExternalHost = resolved;
                return session;
            }

            if (typeof existing === 'boolean') {
                setSessionIsExternalHostFlag(session, existing);
                if (ctx) {
                    ctx.sessionIsExternalHost = existing;
                }
            }

            return session;
        }

        /**
         * Host must set is_external_host on create/schedule/start.
         * Default: assigned host row is not masked.
         * Master Scheduler only: when scheduler ≠ owner and session is external,
         * support host may be stamped use_masked_name=true.
         */
        function stampHostSessionFlags(session, context) {
            if (!session) {
                return $q.when(session);
            }

            var ctxPromise = context
                ? $q.when(context)
                : loadInviteContext().catch(function() { return getCachedContext(); });

            return ctxPromise.then(function(ctx) {
                var inviteCtx = ctx || {};
                var me = Principal.getUserInfo() || {};
                // Instant create often has no owner/owner_info yet — bind creator
                // before resolve so is_external_host uses Principal, not an empty host.
                if (!session.owner && me.id) {
                    session.owner = me.id;
                }
                if (!session.owner_info && me.id &&
                    String(session.owner) === String(me.id)) {
                    session.owner_info = {
                        id: me.id,
                        user_id: me.id,
                        email: me.email,
                        user_type: me.user_type,
                        first_name: me.first_name,
                        last_name: me.last_name
                    };
                }

                applySessionHostFlags(session, inviteCtx);
                if (inviteCtx) {
                    inviteCtx.sessionIsExternalHost = !!getSessionIsExternalHostFlag(session);
                }

                var ownerId = session.owner || me.id;
                var schedulerId = session.scheduler &&
                    (session.scheduler.id || session.scheduler.user_id);
                var hostMask = false;

                // Master Scheduler scheduling only — do not change normal host stamp.
                if (schedulerId && ownerId &&
                    String(schedulerId) !== String(ownerId) &&
                    getSessionIsExternalHostFlag(session) === true) {
                    var supportEntry = getSupportMemberEntry(inviteCtx, ownerId);
                    hostMask = !!(supportEntry && supportEntry.maskingEnabled);
                }

                if (session.session_users && ownerId) {
                    angular.forEach(session.session_users, function(entry) {
                        if (!entry) {
                            return;
                        }
                        if (String(entry.user_id) === String(ownerId)) {
                            setEntryUseMaskedNameFlag(entry, hostMask);
                        }
                    });
                }

                stampSessionHostObject(session, {
                    ownerId: ownerId,
                    creatorId: schedulerId || ownerId,
                    useMaskedName: hostMask
                });

                return sanitizeSessionApiFlags(session);
            });
        }

        /**
         * Reads use_masked_name from session_users[] when backend / invite stamp provides it.
         */
        function getSessionUserMaskFlag(session, userId) {
            return getEntryUseMaskedNameFlag(getSessionUserEntry(session, userId));
        }

        /**
         * Masked display name stamped on session_users (invite / PUT) for receivers.
         */
        function getSessionUserMaskedDisplayName(session, userId) {
            var entry = getSessionUserEntry(session, userId);
            return resolveMaskedDisplayNameValue(entry);
        }

        function getSessionUserEntry(session, userId) {
            if (!session || !session.session_users || userId == null || userId === '') {
                return null;
            }

            var entry = null;
            angular.forEach(session.session_users, function(sessionUser) {
                if (entry || !sessionUser) {
                    return;
                }
                if (String(sessionUser.user_id) === String(userId)) {
                    entry = sessionUser;
                }
            });
            return entry;
        }

        /**
         * True when this participant should publish/display a masked name.
         * Follows Name Resolution diagram (feature → host/participant branches).
         */
        function shouldPublishMaskedName(options) {
            var opts = options || {};
            var published = resolvePublishedDisplayName(opts);
            var participant = opts.participant || {};
            var realName = ((participant.first_name || '') + ' ' + (participant.last_name || '')).trim();
            return !!(published && realName && published !== realName);
        }

        function parseSessionSettings(settings) {
            var externalUserInviteeListEnabled = false;
            var teamIdsSetting = '';

            angular.forEach(settings, function(setting) {
                if (!setting || setting.category !== 'session_setting' || !setting.fields) {
                    return;
                }

                angular.forEach(setting.fields, function(field) {
                    if (field.name === 'externalUserInviteeListEnabled') {
                        externalUserInviteeListEnabled = field.value === 'true';
                    }
                    if (field.name === 'teamIds') {
                        teamIdsSetting = field.value || '';
                    }
                });
            });

            return {
                externalUserInviteeListEnabled: externalUserInviteeListEnabled,
                teamMaskMap: parseTeamIdsString(teamIdsSetting)
            };
        }

        /**
         * Supports:
         * - new: "id1::true,id2::false"
         * - legacy: "id1,id2" (mask defaults to true)
         */
        function parseTeamIdsString(teamIdsString) {
            var teamMaskMap = {};
            if (!teamIdsString || teamIdsString.trim() === '') {
                return teamMaskMap;
            }

            angular.forEach(teamIdsString.split(','), function(entry) {
                var value = (entry || '').trim();
                if (!value) {
                    return;
                }

                if (value.indexOf('::') !== -1) {
                    var parts = value.split('::');
                    var id = parts[0] ? parts[0].trim() : '';
                    if (!id) {
                        return;
                    }
                    teamMaskMap[id] = (parts[1] || '').toLowerCase() !== 'false';
                    return;
                }

                teamMaskMap[value] = true;
            });

            return teamMaskMap;
        }

        function isRestrictedExternalHost(context) {
            var user = context.currentUser || {};
            var enabled = !!context.externalUserInviteeListEnabled;
            var teamIds = context.teamIds || [];
            var userType = ((user && user.user_type) || '').toUpperCase();

            if (!enabled || !teamIds.length) {
                return false;
            }

            // Support Team Member → full workspace invite list.
            if (context.isSupportTeamMember) {
                return false;
            }

            // Domain-match (internal company email / domain-match external) → full list.
            if (isWorkspaceEmailMatch(user, context.workspaceEmailDomain)) {
                return false;
            }

            // PRD: only Standard External is restricted. Explicit INTERNAL keeps full list.
            if (userType === 'INTERNAL') {
                return false;
            }

            // EXTERNAL, or missing user_type with non-workspace email → restricted.
            if (userType === 'EXTERNAL' || !userType) {
                return !!context.workspaceEmailDomain;
            }

            return false;
        }

        function isExternalUser(user) {
            return ((user && user.user_type) || '').toUpperCase() === 'EXTERNAL';
        }

        /**
         * Control Hub may store domains as "aubergine.co" or "@aubergine.co" — treat as equivalent.
         */
        function normalizeEmailDomainEntry(domain) {
            var value = String(domain || '').trim().toLowerCase();
            if (value.charAt(0) === '@') {
                value = value.slice(1).trim();
            }
            return value;
        }

        /**
         * Workspace email_domain may be a single domain or a comma-separated list
         * (e.g. "atheerair.com,aubergine.co"). Match if the user's email domain
         * equals any entry.
         */
        function isWorkspaceEmailMatch(user, workspaceEmailDomain) {
            if (!workspaceEmailDomain) {
                return false;
            }

            var email = ((user && user.email) || '').toLowerCase();
            if (!email || email.indexOf('@') === -1) {
                return false;
            }

            var userDomain = normalizeEmailDomainEntry(email.split('@')[1]);
            if (!userDomain) {
                return false;
            }

            var domains = String(workspaceEmailDomain).toLowerCase().split(',');
            for (var i = 0; i < domains.length; i++) {
                var domain = normalizeEmailDomainEntry(domains[i]);
                if (domain && userDomain === domain) {
                    return true;
                }
            }
            return false;
        }

        function buildMembersQuery(options) {
            var queryParts = [];
            var excludedIds = (options && options.excludedIds) || [];
            var teamIds = (options && options.teamIds) || [];

            if (teamIds.length) {
                queryParts.push('object_id=in=(' + teamIds.join(',') + ')');
            }

            if (excludedIds.length) {
                queryParts.push('id=out=(' + excludedIds.join(',') + ')');
            }

            return queryParts.join(';');
        }

        /**
         * Single call with include_details=true (FOS-20118):
         * - Full UserDTO (custom_attributes, job_title, ...)
         * - segments[] limited to queried object_ids for per-user team membership
         * Masking is derived from segments ∩ teamMaskMap (any true wins).
         */
        function fetchAllSupportMembers(teamIds, teamMaskMap) {
            var ids = teamIds || [];
            if (!ids.length) {
                return $q.when([]);
            }
            return fetchMembersPage(ids).then(function(members) {
                // Stamp masking from segments + settings for downstream decorate.
                return (members || []).map(function(member) {
                    var tagged = angular.copy(member);
                    tagged.teamIds = extractTeamIds(tagged);
                    tagged.masked_display_name = resolveMaskedDisplayNameValue(tagged);
                    tagged.maskingEnabled = isMaskingEnabledForTeams(tagged.teamIds, teamMaskMap);
                    return tagged;
                });
            });
        }

        function fetchMembersPage(teamIds) {
            var allMembers = [];
            var deferred = $q.defer();

            function fetchPage(page) {
                Audience.getMembers({
                    query: buildMembersQuery({ teamIds: teamIds }),
                    page: page,
                    size: MEMBERS_PAGE_SIZE,
                    include_details: true
                }, function(data, headers) {
                    allMembers = allMembers.concat(data || []);
                    var total = parseInt(headers('X-Total-Count'), 10);
                    var loaded = allMembers.length;

                    if (!isNaN(total) && loaded < total) {
                        fetchPage(page + 1);
                    } else if ((data || []).length === MEMBERS_PAGE_SIZE && (isNaN(total) || loaded < total)) {
                        fetchPage(page + 1);
                    } else {
                        deferred.resolve(allMembers);
                    }
                }, function() {
                    deferred.resolve(allMembers);
                });
            }

            fetchPage(0);
            return deferred.promise;
        }

        function isMaskingEnabledForTeams(teamIds, teamMaskMap) {
            var map = teamMaskMap || {};
            for (var i = 0; i < (teamIds || []).length; i++) {
                if (map[teamIds[i]] || map[String(teamIds[i])]) {
                    return true;
                }
            }
            return false;
        }

        /**
         * Merge members by user id. If user belongs to multiple configured teams,
         * maskingEnabled = true when ANY team has masking enabled.
         */
        function buildSupportMemberMap(members, teamMaskMap) {
            var supportMemberMap = {};

            angular.forEach(members || [], function(member) {
                var memberId = member && (member.id || member.user_id);
                if (!member || !memberId) {
                    return;
                }
                memberId = String(memberId);

                var teamIds = uniqueConcat(member.teamIds || [], extractTeamIds(member));
                var maskedName = resolveMaskedDisplayNameValue(member);
                var maskingEnabled = !!member.maskingEnabled;
                if (!maskingEnabled) {
                    maskingEnabled = isMaskingEnabledForTeams(teamIds, teamMaskMap);
                }

                var existing = supportMemberMap[memberId];
                if (existing) {
                    existing.maskingEnabled = existing.maskingEnabled || maskingEnabled;
                    existing.teamIds = uniqueConcat(existing.teamIds, teamIds);
                    if (!existing.masked_display_name && maskedName) {
                        existing.masked_display_name = maskedName;
                    }
                    return;
                }

                var invitee = angular.copy(member);
                invitee.id = memberId;
                invitee.teamIds = teamIds;
                invitee.maskingEnabled = maskingEnabled;
                invitee.masked_display_name = maskedName || invitee.masked_display_name;
                supportMemberMap[memberId] = invitee;
            });

            return supportMemberMap;
        }

        function extractTeamIds(member) {
            var ids = [];

            // FOS-20118 include_details: segments limited to queried object_ids.
            if (angular.isArray(member.segments)) {
                angular.forEach(member.segments, function(segment) {
                    if (segment && segment.id) {
                        ids.push(String(segment.id));
                    }
                });
            }

            if (member.team_id) {
                ids.push(String(member.team_id));
            }
            if (member.object_id) {
                ids.push(String(member.object_id));
            }
            if (member.audience_id) {
                ids.push(String(member.audience_id));
            }
            if (angular.isArray(member.team_ids)) {
                angular.forEach(member.team_ids, function(id) {
                    if (id) {
                        ids.push(String(id));
                    }
                });
            }
            if (angular.isArray(member.audiences)) {
                angular.forEach(member.audiences, function(audience) {
                    if (audience && audience.id) {
                        ids.push(String(audience.id));
                    }
                });
            }

            return uniqueConcat([], ids);
        }

        /**
         * masked_display_name may be top-level or under custom_attributes (UserDTO).
         */
        function resolveMaskedDisplayNameValue(user) {
            if (!user) {
                return '';
            }
            if (user.masked_display_name) {
                return user.masked_display_name;
            }
            var attrs = user.custom_attributes;
            if (attrs && attrs.masked_display_name) {
                return attrs.masked_display_name;
            }
            return '';
        }

        function getMaskedDisplayName(user) {
            return resolveMaskedDisplayNameValue(user) ||
                $filter('translate')(MASKED_NAME_FALLBACK_KEY);
        }

        function uniqueConcat(base, additions) {
            var map = {};
            var result = [];
            angular.forEach((base || []).concat(additions || []), function(id) {
                if (!id || map[id]) {
                    return;
                }
                map[id] = true;
                result.push(id);
            });
            return result;
        }

        function decorateInviteeList(users, context) {
            return (users || []).map(function(user) {
                return decorateInvitee(user, context);
            });
        }

        function getSupportMemberEntry(context, userId) {
            if (!context || !context.supportMemberMap || userId == null || userId === '') {
                return null;
            }
            return context.supportMemberMap[String(userId)] || null;
        }

        function decorateInvitee(user, context) {
            var invitee = angular.copy(user);
            var supportEntry = getSupportMemberEntry(context, invitee.id || invitee.user_id);
            var shouldMask = supportEntry
                ? !!supportEntry.maskingEnabled
                : resolveMaskingFromTeamMap(invitee, (context || {}).teamMaskMap || {});

            if (supportEntry) {
                var supportMasked = resolveMaskedDisplayNameValue(supportEntry);
                if (supportMasked) {
                    invitee.masked_display_name = supportMasked;
                }
                if (supportEntry.job_title && !invitee.job_title) {
                    invitee.job_title = supportEntry.job_title;
                }
            } else {
                var ownMasked = resolveMaskedDisplayNameValue(invitee);
                if (ownMasked) {
                    invitee.masked_display_name = ownMasked;
                }
            }

            invitee.isInviteMasked = shouldMask;
            invitee.maskingEnabled = shouldMask;

            if (shouldMask) {
                invitee.display = getMaskedDisplayName(invitee);
                invitee.name = invitee.display;
                invitee.inviteSubtitle = invitee.job_title || '';
                invitee.email = '';
                var nameParts = invitee.display.trim().split(/\s+/);
                invitee.first_name = nameParts[0] || invitee.display;
                invitee.last_name = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
                // Avatar initials must match masked name (not real first/last or profile photo).
                clearAvatarPictureFields(invitee);
            } else {
                invitee.display = ((invitee.first_name || '') + ' ' + (invitee.last_name || '')).trim();
                invitee.name = invitee.display;
                invitee.inviteSubtitle = invitee.email || '';
            }

            return invitee;
        }

        /**
         * Meeting Detail / schedule chips: restricted external → same as Invite decorate.
         * Other viewers keep real names until FOS-20187 session flags are available.
         */
        function decorateUsersForSessionDetail(users, context, host) {
            var ctx = context || cachedContext;
            if (!ctx || !ctx.externalUserInviteeListEnabled) {
                return (users || []).map(function(user) {
                    var copy = angular.copy(user);
                    copy.display = ((copy.first_name || '') + ' ' + (copy.last_name || '')).trim();
                    return copy;
                });
            }

            if (ctx.isRestrictedExternal) {
                return decorateInviteeList(users, ctx);
            }

            return (users || []).map(function(user) {
                var copy = angular.copy(user);
                copy.display = ((copy.first_name || '') + ' ' + (copy.last_name || '')).trim();
                return copy;
            });
        }

        function resolveMaskingFromTeamMap(user, teamMaskMap) {
            return isMaskingEnabledForTeams(extractTeamIds(user), teamMaskMap);
        }

        /**
         * Local search over cached support members (restricted external invite flow).
         * When a member is masked, match only masked name + job title so real identity is not discoverable.
         */
        function filterMembersLocally(members, searchText, excludedIds) {
            var filter = (searchText || '').trim().toLowerCase();
            var excludedMap = {};
            angular.forEach(excludedIds || [], function(id) {
                if (id) {
                    excludedMap[id] = true;
                }
            });

            return (members || []).filter(function(member) {
                if (!member || !member.id || excludedMap[member.id]) {
                    return false;
                }
                if (!filter) {
                    return true;
                }

                var jobTitle = (member.job_title || '').toLowerCase();
                var masked = (resolveMaskedDisplayNameValue(member) || member.display || '').toLowerCase();
                var fallback = ($filter('translate')(MASKED_NAME_FALLBACK_KEY) || '').toLowerCase();

                if (member.maskingEnabled || member.isInviteMasked) {
                    return masked.indexOf(filter) !== -1 ||
                        fallback.indexOf(filter) !== -1 ||
                        jobTitle.indexOf(filter) !== -1;
                }

                var first = (member.first_name || '').toLowerCase();
                var last = (member.last_name || '').toLowerCase();
                var email = (member.email || '').toLowerCase();
                var full = (first + ' ' + last).trim();

                return first.indexOf(filter) !== -1 ||
                    last.indexOf(filter) !== -1 ||
                    email.indexOf(filter) !== -1 ||
                    jobTitle.indexOf(filter) !== -1 ||
                    masked.indexOf(filter) !== -1 ||
                    full.indexOf(filter) !== -1;
            });
        }

        function paginateMembers(members, page, size) {
            var safePage = page || 0;
            var safeSize = size || 20;
            var start = safePage * safeSize;
            return (members || []).slice(start, start + safeSize);
        }

        /**
         * Classify a user relative to configured support teams / workspace domain.
         * Returns: INTERNAL | EXTERNAL | SUPPORT_TEAM_MEMBER
         */
        function classifyUser(user, context) {
            if (!user) {
                return 'INTERNAL';
            }

            if (context && context.supportMemberMap && context.supportMemberMap[user.id]) {
                return 'SUPPORT_TEAM_MEMBER';
            }

            if (isExternalUser(user)) {
                if (context && isWorkspaceEmailMatch(user, context.workspaceEmailDomain)) {
                    return 'EXTERNAL';
                }
                return 'EXTERNAL';
            }

            return 'INTERNAL';
        }

        /**
         * Name Resolution diagram (Jitsi / PubNub publish):
         * - Feature off → real name
         * - Host + is_external_host + session_host.use_masked_name → masked; else real
         * - Participant guest → real
         * - Participant + !is_external_host → real
         * - Participant + is_external_host + support + team masking → masked
         * - Participant + is_external_host + not support → real
         */
        function resolvePublishedDisplayName(options) {
            var opts = options || {};
            var participant = opts.participant || {};
            var host = opts.host || {};
            var context = opts.context || {};
            var session = opts.session;
            var isHost = !!opts.isHost;
            var isGuest = !!(
                opts.isGuest ||
                participant.is_guest ||
                participant.isGuest
            );
            var realName = ((participant.first_name || '') + ' ' + (participant.last_name || '')).trim();
            var participantId = participant.id || participant.user_id || participant.userId;

            if (!context.externalUserInviteeListEnabled) {
                return realName;
            }

            var sessionIsExternal = isExternalHost(host, context, session);

            if (isHost) {
                if (!sessionIsExternal) {
                    return realName;
                }
                if (getSessionHostUseMaskedName(session) !== true) {
                    return realName;
                }
                return resolveMaskedNameForPublisher(participant, context, participantId, opts);
            }

            if (isGuest) {
                return realName;
            }

            if (!sessionIsExternal) {
                return realName;
            }

            var supportEntry = getSupportMemberEntry(context, participantId);
            if (!supportEntry || !supportEntry.maskingEnabled) {
                return realName;
            }

            return resolveMaskedNameForPublisher(participant, context, participantId, opts);
        }

        function getSessionHostUseMaskedName(session) {
            var hostObj = session && session.session_host;
            if (!hostObj) {
                return undefined;
            }
            if (typeof hostObj.use_masked_name === 'boolean') {
                return hostObj.use_masked_name;
            }
            if (typeof hostObj.useMaskedName === 'boolean') {
                return hostObj.useMaskedName;
            }
            return undefined;
        }

        function resolveMaskedNameForPublisher(participant, context, participantId, opts) {
            var supportEntry = getSupportMemberEntry(context, participantId);
            return getMaskedDisplayName({
                masked_display_name: (supportEntry && resolveMaskedDisplayNameValue(supportEntry)) ||
                    resolveMaskedDisplayNameValue(participant) ||
                    (opts && opts.sessionMaskedDisplayName)
            });
        }

        /**
         * Optional display helper when session_users.useMaskedName is present (FOS-20187).
         * Chat/insights should prefer API fields once backend ships them.
         */
        function resolveVisibleDisplayName(user, context, host, session) {
            if (!user) {
                return '';
            }
            var realName = ((user.first_name || '') + ' ' + (user.last_name || '')).trim();
            var userId = user.id || user.user_id || user.userId;
            if (getSessionUserMaskFlag(session, userId) === true) {
                return getMaskedDisplayName({
                    masked_display_name: getSessionUserMaskedDisplayName(session, userId) ||
                        resolveMaskedDisplayNameValue(user)
                });
            }
            return realName || (user.display || '');
        }

     
        function shouldUseMaskedNameForApiUser(user, session) {
            if (!user) {
                return false;
            }
            if (getSessionIsExternalHostFlag(session) === false) {
                return false;
            }
            var userId = user.id || user.user_id || user.userId;
            var hostObj = session && session.session_host;
            if (hostObj && hostObj.user_id != null && userId != null &&
                String(hostObj.user_id) === String(userId) &&
                typeof hostObj.use_masked_name === 'boolean') {
                return hostObj.use_masked_name;
            }
            var onUser = getEntryUseMaskedNameFlag(user);
            if (typeof onUser === 'boolean') {
                return onUser;
            }
            return getSessionUserMaskFlag(session, userId) === true;
        }

        function resolveApiVisibleDisplayName(user, session) {
            if (!user) {
                return '';
            }
            var realName = ((user.first_name || '') + ' ' + (user.last_name || '')).trim();
            if (!shouldUseMaskedNameForApiUser(user, session)) {
                return realName || (user.display || '');
            }
            return getMaskedDisplayName({
                masked_display_name: resolveMaskedDisplayNameValue(user) ||
                    getSessionUserMaskedDisplayName(
                        session,
                        user.id || user.user_id || user.userId
                    )
            });
        }

        function applyApiVisibleNameToUserInfo(userInfo, session) {
            if (!userInfo) {
                return userInfo;
            }
            if (!shouldUseMaskedNameForApiUser(userInfo, session)) {
                return userInfo;
            }
            var visibleName = resolveApiVisibleDisplayName(userInfo, session);
            var realName = ((userInfo.first_name || '') + ' ' + (userInfo.last_name || '')).trim();
            if (!visibleName || visibleName === realName) {
                clearAvatarPictureFields(userInfo);
                return userInfo;
            }
            var parts = visibleName.trim().split(/\s+/);
            userInfo.first_name = parts[0] || visibleName;
            userInfo.last_name = parts.length > 1 ? parts.slice(1).join(' ') : '';
            // Full masked label for chat under-avatar (template prefers masked_display_name).
            userInfo.masked_display_name = visibleName;
            clearAvatarPictureFields(userInfo);
            return userInfo;
        }

        function resolveCurrentUserPublishedName(host, context, session) {
            var user = Principal.getUserInfo() || {};
            var ctx = context || cachedContext || {};
            var resolvedHost = enrichHostForNameResolution(
                host || nameResolutionHost || {},
                session
            );
            var isHost = !!(
                (resolvedHost.id || resolvedHost.user_id) &&
                user.id &&
                String(resolvedHost.id || resolvedHost.user_id) === String(user.id)
            );
            return resolvePublishedDisplayName({
                participant: user,
                host: resolvedHost,
                context: ctx,
                isHost: isHost,
                isGuest: !!user.is_guest || !!user.isGuest,
                session: session,
                sessionMaskedDisplayName: getSessionUserMaskedDisplayName(session, user.id)
            }) || ((user.first_name || '') + ' ' + (user.last_name || '')).trim();
        }

        function applyVisibleNameToUserInfo(userInfo, context, host, session) {
            if (!userInfo) {
                return userInfo;
            }
            var visibleName = resolveVisibleDisplayName(userInfo, context, host, session);
            var realName = ((userInfo.first_name || '') + ' ' + (userInfo.last_name || '')).trim();
            if (!visibleName || visibleName === realName) {
                return userInfo;
            }
            var parts = visibleName.trim().split(/\s+/);
            userInfo.first_name = parts[0] || visibleName;
            userInfo.last_name = parts.length > 1 ? parts.slice(1).join(' ') : '';
            clearAvatarPictureFields(userInfo);
            return userInfo;
        }

        /**
         * Live Users receiver (Jitsi / PubNub) — FOS-20187 / mobile contract.
         * Prefer runtime fields from peer (useMaskedName + maskedDisplayName),
         * then session_users stamp. Do not re-derive from audiences/members.
         * When masked, firstName/lastName/shortName drive fp-avatar initials; drop photo.
         */
        function applyVisibleNameToLiveSessionUser(sessionUser, context, host, session) {
            if (!sessionUser) {
                return sessionUser;
            }

            var userId = sessionUser.userId || sessionUser.user_id;
            var useMasked = sessionUser.useMaskedName;
            if (typeof useMasked !== 'boolean') {
                useMasked = getSessionUserMaskFlag(session, userId);
            }
            if (useMasked !== true) {
                return sessionUser;
            }

            var maskedName = sessionUser.maskedDisplayName ||
                sessionUser.masked_display_name ||
                getSessionUserMaskedDisplayName(session, userId) ||
                getMaskedDisplayName({});
            var parts = maskedName.trim().split(/\s+/);
            sessionUser.firstName = parts[0] || maskedName;
            sessionUser.lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
            sessionUser.first_name = sessionUser.firstName;
            sessionUser.last_name = sessionUser.lastName;
            sessionUser.useMaskedName = true;
            sessionUser.maskedDisplayName = maskedName;
            sessionUser.shortName = buildShortNameFromDisplayName(
                maskedName,
                sessionUser.shortName
            );
            clearAvatarPictureFields(sessionUser);
            return sessionUser;
        }

        /** Suppress real profile photo so avatar falls back to masked-name initials. */
        function clearAvatarPictureFields(user) {
            if (!user) {
                return;
            }
            user.picture_id = null;
            user.pictureId = null;
        }

        function buildShortNameFromDisplayName(displayName, fallback) {
            var parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
            if (!parts.length) {
                return fallback || '';
            }
            if (parts.length === 1) {
                return (parts[0].charAt(0) || '').toUpperCase();
            }
            return (
                (parts[0].charAt(0) || '') + (parts[parts.length - 1].charAt(0) || '')
            ).toUpperCase();
        }

        /**
         * Whether the *session host* is a restricted external (masking applies to peers).
         * Never use ctx.isRestrictedExternal / ctx.isSupportTeamMember — those describe
         * the current viewer (e.g. support joiner), not the host.
         */
        function isExternalHost(host, context, session) {
            var ctx = context || {};

            if (typeof ctx.sessionIsExternalHost === 'boolean') {
                return ctx.sessionIsExternalHost;
            }

            var sessionFlag = getSessionIsExternalHostFlag(session);
            if (typeof sessionFlag === 'boolean') {
                return sessionFlag;
            }

            var hostUser = host || {};
            var hostId = hostUser.id || hostUser.user_id;
            var hostIsSupport = false;
            if (hostId && ctx.supportMemberMap) {
                hostIsSupport = !!(
                    ctx.supportMemberMap[hostId] ||
                    ctx.supportMemberMap[String(hostId)]
                );
            }

            return isRestrictedExternalHost({
                currentUser: hostUser,
                externalUserInviteeListEnabled: ctx.externalUserInviteeListEnabled,
                teamIds: ctx.teamIds || [],
                workspaceEmailDomain: ctx.workspaceEmailDomain,
                isSupportTeamMember: hostIsSupport
            });
        }
    }
})();
